import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge-utils";

/**
 * App Bridge Initialization
 * 
 * Phase 4.1.1: Real implementation for embedded admin app
 * 
 * This module initializes Shopify App Bridge and provides
 * utilities for session token retrieval.
 */

let appInstance: ReturnType<typeof createApp> | null = null;

// Cache URL parameters to preserve them across navigation
let cachedShop: string | null = null;
let cachedHost: string | null = null;

/**
 * Initialize Shopify App Bridge
 * 
 * Should be called once when the app mounts
 * 
 * @returns App Bridge instance
 */
export function initializeAppBridge(): ReturnType<typeof createApp> {
  if (appInstance) {
    return appInstance;
  }

  // Get shop and host from URL params (provided by Shopify)
  const urlParams = new URLSearchParams(window.location.search);
  const shop = urlParams.get("shop");
  const host = urlParams.get("host");

  if (!shop) {
    throw new Error("Missing required parameter: shop");
  }

  // Cache URL parameters for future use (persists across navigation)
  cachedShop = shop;
  cachedHost = host;

  // API key should be in environment variable
  const apiKey = (import.meta as { env?: { VITE_SHOPIFY_API_KEY?: string } }).env?.VITE_SHOPIFY_API_KEY;

  if (!apiKey) {
    throw new Error("Missing VITE_SHOPIFY_API_KEY environment variable");
  }

  appInstance = createApp({
    apiKey,
    host: host || "",
    forceRedirect: true,
  });

  // Expose utilities globally for debugging
  const shopifyAppUtils = {
    app: appInstance,
    getSessionToken: getShopifySessionToken,
    getShopifySessionToken: getShopifySessionToken,
    isEmbedded: isEmbedded,
    initializeAppBridge: initializeAppBridge,
    getCachedShop: getCachedShop,
    getCachedHost: getCachedHost,
  };
  
  (window as any).shopifyApp = shopifyAppUtils;

  console.log("[App Bridge] Initialized successfully");
  console.log("[App Bridge] Shop:", shop);
  console.log("[App Bridge] Host:", host ? "present" : "missing");
  console.log("[App Bridge] Use window.shopifyApp.getSessionToken() to get token");
  console.log("[App Bridge] Utils exposed:", Object.keys(shopifyAppUtils).join(', '));

  return appInstance;
}

/**
 * Get current App Bridge instance
 * 
 * @returns App Bridge instance or null if not initialized
 */
export function getAppBridgeInstance(): ReturnType<typeof createApp> | null {
  return appInstance;
}

/**
 * Get Shopify session token
 * 
 * This token is a JWT signed by Shopify that can be used
 * to authenticate admin API requests.
 * 
 * @returns Session token (JWT)
 */
export async function getShopifySessionToken(): Promise<string> {
  const app = getAppBridgeInstance();

  if (!app) {
    throw new Error("App Bridge not initialized. Call initializeAppBridge() first.");
  }

  try {
    const token = await getSessionToken(app);
    return token;
  } catch (error) {
    console.error("Failed to get session token:", error);
    throw new Error("Failed to get session token from Shopify");
  }
}

/**
 * Check if running in embedded Shopify admin
 * 
 * Uses cached parameters to work across navigation
 * 
 * @returns true if embedded, false otherwise
 */
export function isEmbedded(): boolean {
  // Use cached parameters if available (persists across navigation)
  if (cachedShop && cachedHost) {
    return true;
  }
  
  // Fallback to URL parameters (first load)
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.has("shop") && urlParams.has("host");
}

/**
 * Get cached shop parameter
 * 
 * @returns Shop domain or null if not initialized
 */
export function getCachedShop(): string | null {
  return cachedShop;
}

/**
 * Get cached host parameter
 * 
 * @returns Host parameter or null if not initialized
 */
export function getCachedHost(): string | null {
  return cachedHost;
}
